
import type { Emblem } from "../net/messages";
import { elem, styleInstaller } from "./chrome";

/** `emblem.MARK_WIDTH` / `MARK_HEIGHT` (`MarkImage.h:13-14`). */
export const EMBLEM_WIDTH = 16;
export const EMBLEM_HEIGHT = 12;
export const EMBLEM_PIXELS = EMBLEM_WIDTH * EMBLEM_HEIGHT;
/** `emblem.MARK_BYTES`: 192 pixels of four bytes. */
export const EMBLEM_BYTES = EMBLEM_PIXELS * 4;

export const PALETTE: readonly (readonly [number, number, number])[] = [
  [0, 0, 0], // 0: the eraser. Alpha 0; the colour is never used.
  [0, 0, 0],
  [255, 255, 255],
  [130, 138, 148],
  [196, 32, 40],
  [240, 122, 48],
  [250, 214, 90],
  [96, 176, 72],
  [32, 120, 96],
  [64, 156, 224],
  [40, 72, 168],
  [136, 88, 200],
  [214, 106, 178],
  [122, 78, 44],
  [232, 190, 148],
];

export interface EmblemActions {
  /** Send this buffer as the guild's emblem. 768 samples, BGRA, in that byte
   *  order because that is the order the original's client sends
   *  (`GuildMarkUploader.cpp:97-99`) and therefore the order `UploadEmblem`
   *  declares. */
  onUpload?: (bgra: number[]) => void;
  /** Ask the server for one guild's emblem. */
  onRequest?: (guildId: number) => void;
}

const STYLE_ID = "emblem-style";

const STYLE = `
.emblem {
  position: fixed;
  left: 264px;
  top: 300px;
  z-index: 58;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(12, 14, 18, 0.86);
  border: 1px solid rgba(253, 255, 124, 0.22);
  pointer-events: auto;
  font: 12px/1.5 system-ui, sans-serif;
  color: #d8dee6;
}
.emblem-title {
  font-size: 0.66rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8fa0b4;
}
.emblem-grid {
  display: grid;
  grid-template-columns: repeat(${EMBLEM_WIDTH}, 14px);
  grid-template-rows: repeat(${EMBLEM_HEIGHT}, 14px);
  gap: 1px;
  margin: 8px 0;
  padding: 3px;
  border-radius: 4px;
  /* A checkerboard, so transparent reads as transparent rather than as black. */
  background:
    repeating-conic-gradient(#2a2f37 0 25%, #1c2027 0 50%) 0 0 / 14px 14px;
}
.emblem-cell { width: 14px; height: 14px; cursor: crosshair; }
.emblem-swatches { display: flex; flex-wrap: wrap; gap: 3px; max-width: 240px; }
.emblem-swatch {
  width: 18px;
  height: 18px;
  border-radius: 3px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  padding: 0;
  cursor: pointer;
}
.emblem-swatch-on { outline: 2px solid rgb(253, 255, 124); outline-offset: 1px; }
.emblem-erase {
  background:
    repeating-conic-gradient(#7a828e 0 25%, #454b55 0 50%) 0 0 / 9px 9px;
}
.emblem-buttons { display: flex; gap: 6px; margin-top: 8px; }
.emblem-button {
  flex: 1;
  padding: 4px 0;
  border-radius: 6px;
  border: 1px solid rgba(253, 255, 124, 0.34);
  background: rgba(253, 255, 124, 0.09);
  color: #f2f0c9;
  font: inherit;
  cursor: pointer;
}
.emblem-button:hover:not(:disabled) { background: rgba(253, 255, 124, 0.18); }
.emblem-button:disabled { opacity: 0.4; cursor: default; }
.emblem-hint { color: #8fa0b4; font-size: 0.9em; max-width: 240px; }
/* MARK_WIDTH x MARK_HEIGHT (MarkImage.h:13-14), at whatever scale the caller
   asked for; the cells themselves are sized by the grid template. */
.emblem-mark { display: grid; flex: 0 0 auto; image-rendering: pixelated; }
.emblem-mark-cell { width: 100%; height: 100%; }
`;

const installStyle = styleInstaller(STYLE_ID, STYLE);

export function indicesFromRgba(rgba: readonly number[]): number[] {
  const out = new Array<number>(EMBLEM_PIXELS).fill(0);
  if (rgba.length !== EMBLEM_BYTES) return out;
  for (let pixel = 0; pixel < EMBLEM_PIXELS; pixel += 1) {
    const base = pixel * 4;
    if (rgba[base + 3] < 128) continue;
    let best = 1;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (let index = 1; index < PALETTE.length; index += 1) {
      const [r, g, b] = PALETTE[index];
      const dr = r - rgba[base];
      const dg = g - rgba[base + 1];
      const db = b - rgba[base + 2];
      const distance = dr * dr + dg * dg + db * db;
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    }
    out[pixel] = best;
  }
  return out;
}

/** Palette indices to the 768 BGRA samples `UploadEmblem` carries. Index 0 is
 *  four zeroes, which is what makes an all-eraser grid the deletion. */
export function bgraFromIndices(indices: readonly number[]): number[] {
  const out = new Array<number>(EMBLEM_BYTES).fill(0);
  for (let pixel = 0; pixel < EMBLEM_PIXELS; pixel += 1) {
    const index = indices[pixel] ?? 0;
    if (index <= 0 || index >= PALETTE.length) continue;
    const [r, g, b] = PALETTE[index];
    const base = pixel * 4;
    out[base] = b;
    out[base + 1] = g;
    out[base + 2] = r;
    out[base + 3] = 255;
  }
  return out;
}

/** `ui.MarkBox` (`uiguild.py:407-412,424-429`): one guild's 16x12 sign, drawn
 *  read-only beside its name. A `MarkBox` in the original is an index into the
 *  shared mark atlas; here it is the `Emblem` frame's own canonical RGBA, which
 *  is the only form this build has and the only one it will serve.
 *
 *  A grid of 192 one-pixel cells rather than a canvas: `happy-dom` has no
 *  canvas, the picture is 192 samples, and a cell carries its own colour so a
 *  test can read the mark back out of the document.
 */
export function markNode(
  rgba: readonly number[],
  doc: Document,
  scale = 1,
): HTMLElement {
  const root = elem(doc, "div", "emblem-mark");
  root.style.width = `${EMBLEM_WIDTH * scale}px`;
  root.style.height = `${EMBLEM_HEIGHT * scale}px`;
  root.style.gridTemplateColumns = `repeat(${EMBLEM_WIDTH}, ${scale}px)`;
  root.style.gridTemplateRows = `repeat(${EMBLEM_HEIGHT}, ${scale}px)`;
  if (rgba.length !== EMBLEM_BYTES) return root;
  for (let pixel = 0; pixel < EMBLEM_PIXELS; pixel += 1) {
    const base = pixel * 4;
    const cell = elem(doc, "span", "emblem-mark-cell");
    // `emblem.canonical_rgba` forces alpha to 0 or 255, so a half value is not
    // a thing this frame can carry and the test is a comparison, not a blend.
    if (rgba[base + 3] >= 128) {
      cell.style.background =
        `rgb(${rgba[base]}, ${rgba[base + 1]}, ${rgba[base + 2]})`;
    }
    root.append(cell);
  }
  return root;
}

function cssFor(index: number): string {
  if (index <= 0 || index >= PALETTE.length) return "transparent";
  const [r, g, b] = PALETTE[index];
  return `rgb(${r}, ${g}, ${b})`;
}

export class EmblemPanel {
  private readonly root: HTMLElement;
  private readonly title: HTMLElement;
  private readonly grid: HTMLElement;
  private readonly cells: HTMLElement[] = [];
  private readonly swatchRow: HTMLElement;
  private readonly swatches: HTMLButtonElement[] = [];
  private readonly buttons: HTMLElement;
  private readonly apply: HTMLButtonElement;
  private readonly revert: HTMLButtonElement;
  private readonly hint: HTMLElement;

  /** What the grid currently shows, as palette indices. */
  private indices: number[] = new Array<number>(EMBLEM_PIXELS).fill(0);

  /** What the server last said the emblem is. Revert goes back to this, so a
   *  master who paints something wrong has a way out that does not need a round
   *  trip. */
  private committed: number[] = new Array<number>(EMBLEM_PIXELS).fill(0);

  private brush = 1;
  private open = false;
  private editable = false;
  /** Which guild the panel is drawing. 0 means none, and hides everything. */
  private guildId = 0;
  private painting = false;

  constructor(
    container: HTMLElement,
    private readonly actions: EmblemActions = {},
  ) {
    const doc = container.ownerDocument;
    installStyle(doc);

    this.root = elem(doc, "div", "emblem");
    this.root.hidden = true;

    this.title = elem(doc, "div", "emblem-title");
    // The 16x12 picture this panel edits is what the original calls the
    // guild's "Sign", not an "emblem" (`GUILD_MARK`/`GUILD_INFO_MARK`,
    // `locale_interface.txt:116,105`) -- the bigger 64x128 flag is a separate
    // upload (`GUILD_SYMBOL`, `:133`) this panel does not touch.
    this.title.textContent = "Guild Sign";

    this.grid = elem(doc, "div", "emblem-grid");
    for (let pixel = 0; pixel < EMBLEM_PIXELS; pixel += 1) {
      const cell = elem(doc, "div", "emblem-cell");
      cell.dataset.pixel = String(pixel);
      cell.addEventListener("pointerdown", () => {
        if (!this.editable) return;
        this.painting = true;
        this.paint(pixel);
      });
      // Drag paints. `pointerenter` rather than `pointermove` so one cell is
      // one event however fast the pointer crosses it.
      cell.addEventListener("pointerenter", () => {
        if (this.painting && this.editable) this.paint(pixel);
      });
      this.cells.push(cell);
      this.grid.append(cell);
    }
    // Released anywhere, including outside the grid: a drag that ends off the
    // panel would otherwise leave the brush stuck down.
    doc.addEventListener("pointerup", () => {
      this.painting = false;
    });

    this.swatchRow = elem(doc, "div", "emblem-swatches");
    for (let index = 0; index < PALETTE.length; index += 1) {
      const swatch = doc.createElement("button");
      swatch.type = "button";
      swatch.className = index === 0 ? "emblem-swatch emblem-erase" : "emblem-swatch";
      swatch.dataset.index = String(index);
      swatch.setAttribute(
        "aria-label",
        index === 0 ? "Eraser" : `Colour ${String(index)}`,
      );
      if (index !== 0) swatch.style.background = cssFor(index);
      swatch.addEventListener("click", () => this.setBrush(index));
      this.swatches.push(swatch);
      this.swatchRow.append(swatch);
    }

    this.buttons = elem(doc, "div", "emblem-buttons");
    this.apply = doc.createElement("button");
    this.apply.type = "button";
    this.apply.className = "emblem-button emblem-apply";
    // `GUILD_INFO_UPLOAD_MARK` (`locale_interface.txt:114`) -- this is the
    // action `UploadEmblem` names, not a generic "Apply".
    this.apply.textContent = "Upload Sign";
    this.apply.addEventListener("click", () => this.submit());
    this.revert = doc.createElement("button");
    this.revert.type = "button";
    this.revert.className = "emblem-button emblem-revert";
    this.revert.textContent = "Revert";
    this.revert.addEventListener("click", () => {
      this.indices = [...this.committed];
      this.repaint();
    });
    this.buttons.append(this.apply, this.revert);

    this.hint = elem(doc, "div", "emblem-hint");
    // Stated because it is the only way to remove a sign and there is
    // deliberately no button for it -- see the file docstring.
    this.hint.textContent =
      "Erase every pixel and press Upload Sign to remove the guild's sign. Only the guild master may change it.";

    this.root.append(this.title, this.grid, this.swatchRow, this.buttons, this.hint);
    container.append(this.root);
    // The legacy mark uploader hides both its transient popup and the editor
    // when Escape is pressed (`uiuploadmark.py:140-146`; the symbol uploader
    // repeats the same close path at `uiuploadmark.py:265-271`). Consume the
    // key here so an open editor does not also reach the world shortcuts.
    this.root.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || !this.showing) return;
      event.preventDefault();
      event.stopPropagation();
      this.open = false;
      this.root.hidden = true;
    });
    this.setBrush(1);
    this.repaint();
  }

  get showing(): boolean {
    return !this.root.hidden;
  }

  /** Whether the palette and buttons are on screen. Master only. For tests. */
  get canEdit(): boolean {
    return this.editable;
  }

  /** The grid's current contents, as palette indices. For tests. */
  get painted(): readonly number[] {
    return this.indices;
  }

  toggle(): void {
    this.open = !this.open;
    this.root.hidden = !this.open || this.guildId === 0;
  }

  setGuild(guildId: number, isMaster: boolean): void {
    const changed = guildId !== this.guildId;
    this.guildId = guildId;
    this.editable = guildId !== 0 && isMaster;
    this.swatchRow.hidden = !this.editable;
    this.buttons.hidden = !this.editable;
    this.hint.hidden = !this.editable;
    this.root.hidden = !this.open || guildId === 0;
    if (changed) {
      // A new guild's emblem is not this one's. Cleared rather than kept, so a
      // master who leaves one guild and founds another does not find the old
      // picture waiting under Revert.
      this.committed = new Array<number>(EMBLEM_PIXELS).fill(0);
      this.indices = [...this.committed];
      this.repaint();
      if (guildId !== 0) this.actions.onRequest?.(guildId);
    }
  }

  update(frame: Emblem): void {
    if (frame.guild_id !== this.guildId) return;
    const next = indicesFromRgba(frame.rgba);
    const dirty = this.indices.some((value, at) => value !== this.committed[at]);
    this.committed = next;
    if (!dirty) {
      this.indices = [...next];
      this.repaint();
    }
  }

  private setBrush(index: number): void {
    this.brush = index;
    for (const swatch of this.swatches) {
      swatch.classList.toggle("emblem-swatch-on", swatch.dataset.index === String(index));
    }
  }

  private paint(pixel: number): void {
    if (this.indices[pixel] === this.brush) return;
    this.indices[pixel] = this.brush;
    this.cells[pixel].style.background = cssFor(this.brush);
  }

  private repaint(): void {
    for (let pixel = 0; pixel < EMBLEM_PIXELS; pixel += 1) {
      this.cells[pixel].style.background = cssFor(this.indices[pixel]);
    }
  }

  private submit(): void {
    if (!this.editable) return;
    // Sent whether or not anything changed. A no-op upload is one frame and the
    // server's own answer; a panel that decided nothing had changed would be
    // deciding it against a picture it may have been told about late.
    this.actions.onUpload?.(bgraFromIndices(this.indices));
  }
}
